import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import * as XLSX from 'xlsx';
import ColumnMapperModal from './ColumnMapperModal';
import EditableCell from './EditableCell';
import './modernized_app.css';   // ← pull in stylesheet
import moment from 'moment';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import IssuesLog from './IssuesLog';
import LaunchSuccessAdvisorModal, { getLaunchPredictionBadgeLabel } from './LaunchSuccessAdvisorModal';
import {
  compareTableValues,
  getNextSortConfig,
  getSortIndicator,
  getUniqueOptions,
  matchesColumnFilter,
  matchesDateFilter,
  matchesSelectFilter,
  matchesTimeRangeFilter,
} from './tableUtils';
import { ClearableInput, ClearableRangeInputs, FilterToolbar } from './FilterControls';
import { AIRCRAFT_STATUS_OPTIONS } from './militaryOptions';

// Define the available columns for ATO schedule
const initialColumns = [
  { key: 'takeoffDate', label: 'Takeoff Date', visible: true },
  { key: 'lineNum', label: 'Line #', visible: true },
  { key: 'callSign', label: 'Call Sign', visible: true },
  { key: 'tail', label: 'Tail #', visible: true },
  { key: 'priority', label: 'Priority', visible: true },
  { key: 'spot', label: 'Spot', visible: true },
  { key: 'config', label: 'Config', visible: true },
  { key: 'acftStatus', label: 'ACFT Status', visible: true },
  { key: 'fuelGoal', label: 'Fuel Goal', visible: true },
  { key: 'missionType', label: 'Mission Type', visible: true },
  { key: 'schedMxShow', label: 'Sched MX Show', visible: true },
  { key: 'schedCrewShow', label: 'Sched Crew Show', visible: true },
  { key: 'schedEngStrt', label: 'Sched Eng Start', visible: true },
  { key: 'schedTaxi', label: 'Sched Taxi', visible: true },
  { key: 'schedTakeoff', label: 'Sched Take-off', visible: true },
  { key: 'schedLand', label: 'Sched Land', visible: true },
  { key: 'landingCode', label: 'Landing Code', visible: true },
  { key: 'launchPersonnel', label: 'Launch Personnel', visible: true },
  { key: 'recoveryPersonnel', label: 'Recovery Personnel', visible: true },
  { key: 'uploadedBy', label: 'Uploaded By', visible: false },
  { key: 'lastUploadedAt', label: 'Last Uploaded', visible: true },
];

const DEFAULT_COLLAPSE_COLUMN_KEYS = [
  'callSign',
  'priority',
  'config',
  'fuelGoal',
  'missionType',
  'landingCode',
  'uploadedBy',
  'lastUploadedAt',
];
const FLY_SCHEDULE_MANAGE_ROLES = new Set(['management', 'production', 'prosuper', 'expediter', 'siteadmin']);

function normalizeAtoTailDraft(value) {
  return String(value ?? '').trim().toUpperCase();
}

function formatAtoInlineAlertMessage(alert, row, formatTimestamp) {
  const field = String(alert?.field || '').trim();
  const line = row?.lineNum || row?.line_num || alert?.lineNum || alert?.line_num || '--';
  if (field === 'tail') {
    const fromTail = normalizeAtoTailDraft(alert?.old_value || row?.tail || '');
    const toTail = normalizeAtoTailDraft(alert?.new_value || alert?.tail || '');
    return `Line ${line} tail changed from ${fromTail || '--'} to ${toTail || '--'}`;
  }
  const timeFields = [
    'actMxShow', 'actCrewShow', 'actEngStrt',
    'actTaxi', 'actTakeoff', 'actLand'
  ];
  let displayValue = alert?.new_value;
  if (timeFields.includes(field)) {
    displayValue = formatTimestamp(alert?.new_value).split(' ')[1];
  }
  return `[${alert?.tail || row?.tail || '--'}] ${field}: ${displayValue ?? '--'}`;
}

function TailPickerCell({
  value,
  options = [],
  onSave,
  readOnly = false,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => normalizeAtoTailDraft(value));
  const [menuPosition, setMenuPosition] = useState(null);
  const inputRef = useRef(null);
  const optionTails = useMemo(() => new Set(options.map(option => normalizeAtoTailDraft(option.tail))), [options]);
  const normalizedValue = normalizeAtoTailDraft(value);
  const displayedOptions = useMemo(() => {
    const search = normalizeAtoTailDraft(draft);
    const exact = optionTails.has(search);
    const matches = search
      ? options.filter(option => (
        option.tail.includes(search) ||
        String(option.label || '').toUpperCase().includes(search)
      ))
      : options;
    const rows = matches.length ? matches : options;
    const limited = rows.slice(0, 40);
    if (search && !exact) {
      return [{ tail: search, label: 'typed manually', manual: true }, ...limited];
    }
    return limited;
  }, [draft, optionTails, options]);

  useEffect(() => {
    if (!editing) {
      setDraft(normalizeAtoTailDraft(value));
    }
  }, [value, editing]);

  useEffect(() => {
    if (!editing) {
      setMenuPosition(null);
      return undefined;
    }
    const updateMenuPosition = () => {
      const rect = inputRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.min(Math.max(220, rect.width), window.innerWidth - 16);
      const maxHeight = Math.min(260, window.innerHeight - 24);
      const belowTop = rect.bottom + 5;
      const opensUp = belowTop + maxHeight > window.innerHeight && rect.top > maxHeight + 12;
      const top = opensUp ? rect.top - maxHeight - 5 : Math.min(belowTop, window.innerHeight - maxHeight - 8);
      const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
      setMenuPosition({ top: Math.max(8, top), left, width, maxHeight });
    };
    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [editing, draft, displayedOptions.length]);

  const cancelEdit = () => {
    setDraft(normalizeAtoTailDraft(value));
    setEditing(false);
  };

  const attemptSave = () => {
    const nextValue = normalizeAtoTailDraft(draft);
    if (nextValue !== normalizeAtoTailDraft(value)) {
      onSave(nextValue);
    }
    setEditing(false);
  };

  const chooseTail = nextValue => {
    const normalizedNext = normalizeAtoTailDraft(nextValue);
    if (normalizedNext !== normalizedValue) {
      onSave(normalizedNext);
    }
    setEditing(false);
  };

  if (readOnly) {
    return <div className="editable-cell editable-cell--readonly ato-tail-picker__display">{value || ''}</div>;
  }

  if (editing) {
    return (
      <div className="ato-tail-picker" onClick={event => event.stopPropagation()}>
        <input
          ref={inputRef}
          type="text"
          value={draft}
          autoFocus
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="characters"
          spellCheck={false}
          data-lpignore="true"
          data-form-type="other"
          placeholder="Pick or type tail"
          onChange={event => setDraft(event.target.value)}
          onBlur={attemptSave}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              attemptSave();
            } else if (event.key === 'Escape') {
              cancelEdit();
            }
          }}
        />
        {menuPosition ? createPortal(<div
          className="ato-tail-picker__menu"
          role="listbox"
          aria-label="Status tails"
          style={{
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
            width: `${menuPosition.width}px`,
            maxHeight: `${menuPosition.maxHeight}px`,
          }}
          onMouseDown={event => event.preventDefault()}
          onClick={event => event.stopPropagation()}
        >
          {displayedOptions.map(option => (
            <button
              key={`${option.manual ? 'manual' : 'status'}-${option.tail}`}
              type="button"
              className={option.tail === normalizedValue ? 'is-selected' : ''}
              onMouseDown={event => {
                event.preventDefault();
                chooseTail(option.tail);
              }}
            >
              <strong>{option.tail}</strong>
              {option.label ? <span>{option.label}</span> : null}
            </button>
          ))}
          {!displayedOptions.length ? <span>No Status tails available. Type a tail and press Enter.</span> : null}
        </div>, document.body) : null}
      </div>
    );
  }

  return (
    <button
      type="button"
      className={`editable-cell ato-tail-picker__display${value ? '' : ' editable-cell--empty'}`}
      onClick={event => {
        event.stopPropagation();
        setEditing(true);
      }}
      title="Choose a Status tail or type a tail manually"
    >
      <span>{value || 'Click to add'}</span>
      <span className="ato-tail-picker__cue" aria-hidden="true">{value ? 'Change' : 'Choose tail'}</span>
    </button>
  );
}

const mergeAtoColumns = savedColumns => {
  if (!Array.isArray(savedColumns)) return initialColumns;

  const defaultByKey = new Map(initialColumns.map(col => [col.key, col]));
  const merged = [];

  savedColumns.forEach(savedCol => {
    const savedKey = savedCol?.key;
    const defaultCol = defaultByKey.get(savedKey);
    if (!defaultCol) return;

    merged.push({
      ...defaultCol,
      visible:
        typeof savedCol.visible === 'boolean'
          ? savedCol.visible
          : defaultCol.visible,
    });

    defaultByKey.delete(savedKey);
  });

  defaultByKey.forEach((col, key) => {
    const defaultIndex = initialColumns.findIndex(candidate => candidate.key === key);
    const precedingKeys = new Set(
      initialColumns.slice(0, defaultIndex).map(candidate => candidate.key)
    );
    let insertAfter = -1;
    merged.forEach((candidate, index) => {
      if (precedingKeys.has(candidate.key)) insertAfter = index;
    });
    merged.splice(insertAfter + 1, 0, col);
  });

  return merged;
};

const mergeAtoCollapseSettings = (value, savedColumns) => {
  const defaults = {
    collapsed: false,
    hiddenKeys: DEFAULT_COLLAPSE_COLUMN_KEYS,
  };

  if (!value || typeof value !== 'object') return defaults;

  const validKeys = new Set(initialColumns.map(col => col.key));
  const hiddenKeys = Array.isArray(value.hiddenKeys)
    ? value.hiddenKeys.filter(key => validKeys.has(key))
    : defaults.hiddenKeys;
  const savedColumnKeys = Array.isArray(savedColumns)
    ? new Set(savedColumns.map(column => column?.key))
    : new Set();
  const isLegacyColumnPreference = savedColumnKeys.size > 0 && !savedColumnKeys.has('landingCode');
  const mergedHiddenKeys = isLegacyColumnPreference && !hiddenKeys.includes('landingCode')
    ? [...hiddenKeys, 'landingCode']
    : hiddenKeys;

  return {
    collapsed: Boolean(value.collapsed),
    hiddenKeys: mergedHiddenKeys.length ? mergedHiddenKeys : defaults.hiddenKeys,
  };
};

const normalizeRoleName = value => {
  const normalized = String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
  if (normalized === 'admin') return 'siteadmin';
  return normalized;
};

const userCanManageFlySchedule = (user, personnelData = []) => {
  if (user?.is_superuser) return true;
  const roles = new Set([normalizeRoleName(user?.role)]);
  const userEmail = normalizeRoleName(user?.email);
  const personnelMatch = personnelData.find(person => normalizeRoleName(person.Email || person.email) === userEmail);
  if (personnelMatch?.Role) roles.add(normalizeRoleName(personnelMatch.Role));
  return Array.from(roles).some(role => FLY_SCHEDULE_MANAGE_ROLES.has(role));
};

const userCanOverrideLaunchWeather = (user, personnelData = []) => {
  if (user?.is_superuser) return true;
  const roles = new Set([normalizeRoleName(user?.role)]);
  const userEmail = normalizeRoleName(user?.email);
  const personnelMatch = personnelData.find(person => normalizeRoleName(person.Email || person.email) === userEmail);
  if (personnelMatch?.Role) roles.add(normalizeRoleName(personnelMatch.Role));
  const userShop = normalizeRoleName(user?.shop || personnelMatch?.['Shop Name'] || personnelMatch?.shopName);
  return Array.from(roles).some(role => FLY_SCHEDULE_MANAGE_ROLES.has(role)) || userShop === 'production';
};

const getPersonnelEmployeeNumber = person => String(person?.['Emp Num'] ?? '').trim();
const personnelSelectionIncludes = (selection, employeeNumber) => (
  Boolean(employeeNumber) && (selection || []).some(value => String(value ?? '').trim() === employeeNumber)
);

const getAtoStatusTone = status => {
  const normalized = String(status || '').trim().toUpperCase();
  if (normalized === 'CANN') return 'cann';
  if (normalized.startsWith('P')) return 'p';
  if (normalized.startsWith('FMC')) return 'mc';
  if (normalized.startsWith('NMC')) return 'nmc';
  return 'neutral';
};


const pad2 = (n) => String(n).padStart(2, "0");
const scheduleColumnKeys = [
  'schedMxShow',
  'schedCrewShow',
  'schedEngStrt',
  'schedTaxi',
  'schedTakeoff',
  'schedLand',
];

const scheduleColumnKeySet = new Set(scheduleColumnKeys);
const getAtoColumnClassName = key =>
  `ato-col-${String(key || '').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;
const scheduleOffsetKeyMap = {
  schedMxShow: 'schedMxShowOffsetDays',
  schedCrewShow: 'schedCrewShowOffsetDays',
  schedEngStrt: 'schedEngStrtOffsetDays',
  schedTaxi: 'schedTaxiOffsetDays',
  schedLand: 'schedLandOffsetDays',
};
const scheduleUtcKeyMap = {
  schedMxShow: 'schedMxShowUtc',
  schedCrewShow: 'schedCrewShowUtc',
  schedEngStrt: 'schedEngStrtUtc',
  schedTaxi: 'schedTaxiUtc',
  schedTakeoff: 'schedTakeoffUtc',
  schedLand: 'schedLandUtc',
};
const milestoneScheduleToActualKeyMap = {
  schedMxShow: 'actMxShow',
  schedCrewShow: 'actCrewShow',
  schedEngStrt: 'actEngStrt',
  schedTaxi: 'actTaxi',
  schedTakeoff: 'actTakeoff',
};
const milestoneScheduleKeySet = new Set(Object.keys(milestoneScheduleToActualKeyMap));
const DEFAULT_LAUNCH_PRESET = {
  schedMxShowOffsetMinutes: 150,
  schedCrewShowOffsetMinutes: 90,
  schedEngStrtOffsetMinutes: 35,
  schedTaxiOffsetMinutes: 20,
};
const LAUNCH_PRESET_FIELDS = [
  { key: 'schedMxShowOffsetMinutes', label: 'MX Show', detail: 'minutes before takeoff' },
  { key: 'schedCrewShowOffsetMinutes', label: 'Crew Show', detail: 'minutes before takeoff' },
  { key: 'schedEngStrtOffsetMinutes', label: 'Engine Start', detail: 'minutes before takeoff' },
  { key: 'schedTaxiOffsetMinutes', label: 'Taxi', detail: 'minutes before takeoff' },
];

const shouldShowAtoRowForDate = (row, selectedDate, showCompleted) => {
  if (row.spareStatus === 'consumed') {
    return showCompleted && (!row.takeoffDate || moment(row.takeoffDate).isSame(selectedDate, "day"));
  }

  if (!row.takeoffDate) return true;

  const takeoff = moment(row.takeoffDate);
  const isSameDay = takeoff.isSame(selectedDate, "day");
  const isBeforeDay = takeoff.isBefore(selectedDate, "day");
  const isIncomplete = !row.actLand;
  const isCancelled = Boolean(row.cancelled);
  const isLanded = Boolean(row.actLand);
  const isComplete = isLanded || (isCancelled && !isSameDay);
  const includeByDate = isSameDay || (isBeforeDay && isIncomplete && !isCancelled);

  if (!showCompleted && isComplete) return false;
  return includeByDate;
};

const isLaunchPredictionCandidateRow = row => Boolean(
  row?.id &&
  !row.cancelled &&
  !row.actTakeoff &&
  !row.actLand &&
  row.spareStatus !== 'consumed'
);

const validateScheduledTimeValue = value => {
  const trimmed = String(value ?? '').trim();

  if (trimmed === '') {
    return { isValid: true, value: '' };
  }

  if (!/^([01]\d|2[0-3]):([0-5]\d)$/.test(trimmed)) {
    return {
      isValid: false,
      message: 'Use HH:MM in 24-hour time',
    };
  }

  return { isValid: true, value: trimmed };
};

const validateScheduledOffsetValue = value => {
  const normalized = Number(value);
  if (!Number.isInteger(normalized) || normalized < -2 || normalized > 2) {
    return {
      isValid: false,
      message: 'Offset must be between -2 and +2 days',
    };
  }

  return { isValid: true, value: normalized };
};

const validatePriorityValue = value => {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return { isValid: true, value: '' };
  if (!/^[A-Za-z0-9]{1,5}$/.test(trimmed)) {
    return {
      isValid: false,
      message: 'Use up to 5 letters or numbers',
    };
  }
  return { isValid: true, value: trimmed };
};

const getAtoMilestoneTone = deltaMinutes => {
  if (deltaMinutes <= 1) return 'on-time';
  if (deltaMinutes <= 15) return 'warning';
  return 'late';
};

const parseScheduledMilestoneDateTime = (row, scheduleKey) => {
  const utcKey = scheduleUtcKeyMap[scheduleKey];
  if (utcKey && row[utcKey]) {
    const utcValue = new Date(row[utcKey]);
    if (!Number.isNaN(utcValue.getTime())) return utcValue;
  }

  const dateKey = row.takeoffDate;
  const timeValue = row[scheduleKey];
  const offsetValue = Number(row[scheduleOffsetKeyMap[scheduleKey]] || 0);
  if (!dateKey || !timeValue) return null;

  const parsed = moment(`${dateKey} ${timeValue}`, 'YYYY-MM-DD HH:mm', true);
  if (!parsed.isValid()) return null;
  if (Number.isInteger(offsetValue) && offsetValue !== 0) {
    parsed.add(offsetValue, 'days');
  }

  return parsed.toDate();
};

const parseActualMilestoneDateTime = value => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getAtoMilestoneStatus = (row, scheduleKey, nowValue) => {
  const actualKey = milestoneScheduleToActualKeyMap[scheduleKey];
  if (!actualKey || row.cancelled || row.spareStatus === 'consumed') return null;

  const scheduledAt = parseScheduledMilestoneDateTime(row, scheduleKey);
  if (!scheduledAt) return null;

  const actualAt = parseActualMilestoneDateTime(row[actualKey]);

  if (actualAt) {
    const deltaMinutes = (actualAt.getTime() - scheduledAt.getTime()) / 60000;
    return {
      mode: 'complete',
      tone: getAtoMilestoneTone(deltaMinutes),
      deltaMinutes,
    };
  }

  const overdueMinutes = (nowValue - scheduledAt.getTime()) / 60000;
  if (overdueMinutes < 0) return null;

  return {
    mode: 'pending',
    tone: getAtoMilestoneTone(overdueMinutes),
    deltaMinutes: overdueMinutes,
  };
};

const getAtoMilestoneStatusTitle = status => {
  if (!status) return undefined;

  const roundedMinutes = Math.max(Math.round(Math.abs(status.deltaMinutes)), 0);

  if (status.mode === 'complete') {
    if (status.deltaMinutes < 0) {
      return `Actual entered: ${roundedMinutes} min early`;
    }

    if (status.deltaMinutes <= 1) {
      return 'Actual entered: on time or within 1 minute late';
    }

    return `Actual entered: ${Math.round(status.deltaMinutes)} min late`;
  }

  if (status.deltaMinutes <= 1) {
    return 'Actual not yet entered: due now or within 1 minute late';
  }

  return `Actual not yet entered: ${Math.round(status.deltaMinutes)} min overdue`;
};

const getScheduleOffsetBadgeLabel = offsetValue => {
  const normalized = Number(offsetValue) || 0;
  if (normalized > 0) return `+${normalized}`;
  return `${normalized}`;
};

const scheduleOffsetCycle = [0, 1, 2, -2, -1];

const getNextScheduleOffsetValue = currentOffset => {
  const normalized = Number.isInteger(Number(currentOffset))
    ? Number(currentOffset)
    : 0;
  const currentIndex = scheduleOffsetCycle.indexOf(normalized);
  const safeIndex = currentIndex === -1 ? 0 : currentIndex;
  return scheduleOffsetCycle[(safeIndex + 1) % scheduleOffsetCycle.length];
};

const getPreviousScheduleOffsetValue = currentOffset => {
  const normalized = Number.isInteger(Number(currentOffset))
    ? Number(currentOffset)
    : 0;
  const currentIndex = scheduleOffsetCycle.indexOf(normalized);
  const safeIndex = currentIndex === -1 ? 0 : currentIndex;
  return scheduleOffsetCycle[
    (safeIndex - 1 + scheduleOffsetCycle.length) % scheduleOffsetCycle.length
  ];
};

const getAtoDefaultSortValue = row => {
  const dateValue = row?.takeoffDate || '';
  const takeoffTime = row?.schedTakeoff || '99:99';
  const lineValue = row?.lineNum || '';
  return `${dateValue}|${takeoffTime}|${lineValue}`;
};

const compareAtoDefaultSort = (left, right) => (
  getAtoDefaultSortValue(left).localeCompare(
    getAtoDefaultSortValue(right),
    undefined,
    { numeric: true }
  )
);

const toUTCISOString = (localDateTimeStr) => {
  if (!localDateTimeStr) return null;

  // Parse datetime-local explicitly as LOCAL time
  const [datePart, timePart] = localDateTimeStr.split("T");
  if (!datePart || !timePart) return null;

  const [year, month, day] = datePart.split("-").map(Number);
  const [hour, minute] = timePart.split(":").map(Number);

  const localDt = new Date(year, month - 1, day, hour, minute, 0, 0);
  if (Number.isNaN(localDt.getTime())) return null;

  return localDt.toISOString(); // UTC/Zulu for DB/API
};

const normalizeIsoMinute = value => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  parsed.setSeconds(0, 0);
  return parsed.toISOString();
};

const toLocalInputFormat = (value) => {
  if (!value) return "";

  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "";

  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}T${pad2(dt.getHours())}:${pad2(dt.getMinutes())}`;
};
  
// Component for column settings button on ATO tab
const ColumnSettings = ({ columns, setColumns, onClose }) => {
  // Function for handling drag end
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const newOrder = Array.from(columns);
    const [moved] = newOrder.splice(result.source.index, 1);
    newOrder.splice(result.destination.index, 0, moved);
    setColumns(newOrder);
  };

  // Toggle a column's visibility
  const toggleVisibility = (key) => {
    const newColumns = columns.map(col =>
      col.key === key ? { ...col, visible: !col.visible } : col
    );
    setColumns(newColumns);
  };

  return (
    <div className="ato-column-settings" role="dialog" aria-label="Fly Schedule field settings">
      <div className="ato-column-settings__header">
        <strong>Fly Schedule Fields</strong>
        <button type="button" onClick={onClose}>Close</button>
      </div>
      <p>Choose which fields show in the Fly Schedule table. Drag rows to change their order.</p>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="columns">
          {(provided) => (
            <ul
              className="ato-column-settings__list"
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {columns.map((col, index) => (
                <Draggable key={col.key} draggableId={col.key} index={index}>
                  {(provided) => (
                    <li
                      className="ato-column-settings__item"
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={provided.draggableProps.style}
                    >
                      <input
                        type="checkbox"
                        checked={col.visible}
                        onChange={() => toggleVisibility(col.key)}
                      />
                      <span>{col.label}</span>
                    </li>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </ul>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

const CollapseSettings = ({ columns, hiddenKeys, setHiddenKeys, onClose }) => {
  const visibleColumns = columns.filter(col => col.visible);
  const hiddenKeySet = new Set(hiddenKeys);

  const toggleHiddenKey = (key) => {
    setHiddenKeys(prev => (
      prev.includes(key)
        ? prev.filter(existingKey => existingKey !== key)
        : [...prev, key]
    ));
  };

  return (
    <div className="ato-column-settings" role="dialog" aria-label="Fly Schedule collapse settings">
      <div className="ato-column-settings__header">
        <strong>Collapse View</strong>
        <button type="button" onClick={onClose}>Close</button>
      </div>
      <p>Choose which currently visible fields are temporarily hidden when the Fly Schedule is collapsed.</p>
      <ul className="ato-column-settings__list">
        {visibleColumns.map(col => (
          <li key={col.key} className="ato-column-settings__item ato-column-settings__item--static">
            <input
              type="checkbox"
              checked={hiddenKeySet.has(col.key)}
              onChange={() => toggleHiddenKey(col.key)}
            />
            <span>{col.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

// Styles for the Edit ATO modal
const modalOverlayStyle = {
  position: 'fixed',
  top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

// Styles for the Edit ATO modal
const modalContentStyle = {
  background: '#fff',
  padding: '0',
  borderRadius: '12px',
  maxWidth: '760px',
  width: '94%',
  maxHeight: '90%',
  overflowY: 'auto',
};


export default function ATOscreen({
  api,
  user,
  selectedBase,
  globalView,
  editedCells,
  setEditedCells,
  personnelData,
  fetchData,
  alerts,
  setAlerts,
  selectedAtoRow,
  setSelectedAtoRow,
  handleSelectAtoRow,
  issues,
  tableSettings,
  onTableSettingsChange,
}) {

  // ATO date-picker state
  const today = new Date().toLocaleDateString("en-CA"); // “YYYY-MM-DD”
  const [selectedDate, setSelectedDate] = useState(today);
  const [nowTick, setNowTick] = useState(() => Date.now());
  
  // default to hiding completed flights
  const [showCompleted, setShowCompleted] = useState(false);

  //columns for ATO schedule tab
  const [columns, setColumns] = useState(() => mergeAtoColumns(tableSettings?.columns));
  const [showSettings, setShowSettings] = useState(false);
  const [showCollapseSettings, setShowCollapseSettings] = useState(false);
  const [collapseSettings, setCollapseSettings] = useState(() => mergeAtoCollapseSettings(tableSettings?.collapse, tableSettings?.columns));
  const [sortConfig, setSortConfig] = useState(null);
  const [columnFilters, setColumnFilters] = useState({});

  useEffect(() => {
    if (selectedAtoRow?.takeoffDate) {
      setSelectedDate(String(selectedAtoRow.takeoffDate).slice(0, 10));
    }
  }, [selectedAtoRow?.id, selectedAtoRow?.takeoffDate]);

  const persistTableSettings = (nextColumns, nextCollapseSettings) => {
    if (typeof onTableSettingsChange !== 'function') return;
    onTableSettingsChange({
      columns: nextColumns,
      collapse: nextCollapseSettings,
    });
  };

  const handleColumnsChange = nextColumns => {
    setColumns(nextColumns);
    persistTableSettings(nextColumns, collapseSettings);
  };

  const handleCollapseSettingsChange = updater => {
    setCollapseSettings(prev => {
      const nextCollapseSettings = typeof updater === 'function' ? updater(prev) : updater;
      persistTableSettings(columns, nextCollapseSettings);
      return nextCollapseSettings;
    });
  };

  const [editModalOpen, setEditModalOpen] = useState(false);
  // ATO “Edit Entry” modal
  const [editValues, setEditValues] = useState({});
  const [swapSelectedSpareId, setSwapSelectedSpareId] = useState('');
  const [swapReason, setSwapReason] = useState('');
  const [atoData, setAtoData] = useState([]);
  const [aircraftStatusRows, setAircraftStatusRows] = useState([]);
  const [launchPredictions, setLaunchPredictions] = useState([]);
  const [selectedLaunchPrediction, setSelectedLaunchPrediction] = useState(null);
  const [launchFeedback, setLaunchFeedback] = useState({ accepted: '', feedbackAction: '', feedbackReason: '' });
  const [isWeatherOverrideSaving, setIsWeatherOverrideSaving] = useState(false);
  const [originalSnapshot, setOriginalSnapshot] = useState({});
  
  // Tracking which ATO row is selected
  const [selectedAtoIndex, setSelectedAtoIndex] = useState(null);
  const [editIndex, setEditIndex] = useState(null);  

  // hooks for ATO user made upload file
  const [showMapper, setShowMapper] = useState(false);
  const [excelHeaders, setExcelHeaders] = useState([]);
  const [pendingFile, setPendingFile] = useState(null);
  const [sheetName, setSheetName] = useState('');
  const [mapping, setMapping]   = useState({});    

  // for ato banner notifications like "saved successfully"
  const [notification, setNotification] = useState('');  
  const [isAutoAssigningLaunch, setIsAutoAssigningLaunch] = useState(false);
  const [isAutoAssigningRecovery, setIsAutoAssigningRecovery] = useState(false);
  const [featureFlags, setFeatureFlags] = useState({});
  const [launchPreset, setLaunchPreset] = useState(DEFAULT_LAUNCH_PRESET);
  const [launchPresetDraft, setLaunchPresetDraft] = useState(DEFAULT_LAUNCH_PRESET);
  const [showLaunchPresetModal, setShowLaunchPresetModal] = useState(false);
  const [launchPresetLoading, setLaunchPresetLoading] = useState(false);
  const [launchPresetSaving, setLaunchPresetSaving] = useState(false);
  const [launchPresetError, setLaunchPresetError] = useState('');
  const canManageFlySchedule = userCanManageFlySchedule(user, personnelData);
  const canOverrideLaunchWeather = userCanOverrideLaunchWeather(user, personnelData);
  const recoverySchedulingEnabled = Boolean(featureFlags.recovery_personnel_scheduling);
  const launchSuccessAdvisorEnabled = Boolean(featureFlags.launch_success_advisor);
  const notificationStyle = {
    position: 'fixed',
    top: '10px',
    right: '10px',
    background: '#4caf50',
    color: '#fff',
    padding: '10px 20px',
    borderRadius: '4px',
    zIndex: 1100,
  };

  // upload ATO files refs
  const defaultAtoRef = useRef();
  const customAtoRef  = useRef();
  const tableShellRef = useRef(null);
  const stickyScrollbarRef = useRef(null);
  const stickyScrollbarInnerRef = useRef(null);
  const [showStickyScrollbar, setShowStickyScrollbar] = useState(false);

  // ato template file input
  const atoInputId = "ATO-Template-Upload";
  
  // Filter visible columns from columns state, then apply the temporary collapse view.
  const fieldVisibleColumns = columns.filter(col => col.visible);
  const collapseHiddenKeySet = useMemo(
    () => new Set(collapseSettings.hiddenKeys),
    [collapseSettings.hiddenKeys]
  );
  const visibleColumns = collapseSettings.collapsed
    ? fieldVisibleColumns.filter(col => !collapseHiddenKeySet.has(col.key))
    : fieldVisibleColumns;
  const filterableColumns = visibleColumns;
  const uploadedByOptions = getUniqueOptions(atoData, row => row.uploadedBy);
  const aircraftStatusByTail = useMemo(() => {
    const rowsByTail = new Map();
    aircraftStatusRows.forEach(row => {
      const tailKey = String(row.tail || '').trim().toUpperCase();
      if (tailKey) rowsByTail.set(tailKey, row);
    });
    return rowsByTail;
  }, [aircraftStatusRows]);
  const statusTailOptions = useMemo(() => (
    Array.from(aircraftStatusByTail.values())
      .map(row => {
        const tail = normalizeAtoTailDraft(row.tail);
        return {
          tail,
          label: [
            row.mds,
            row.status,
            row.spot ? `Spot ${row.spot}` : '',
            globalView && row.location ? row.location : '',
          ].filter(Boolean).join(' | '),
        };
      })
      .filter(option => option.tail)
      .sort((left, right) => left.tail.localeCompare(right.tail, undefined, { numeric: true }))
  ), [aircraftStatusByTail, globalView]);
  const launchPredictionByAtoId = useMemo(() => {
    const map = new Map();
    launchPredictions.forEach(prediction => {
      if (prediction?.atoId) map.set(String(prediction.atoId), prediction);
    });
    return map;
  }, [launchPredictions]);
  const spareSwapOptions = useMemo(() => {
    if (!editValues?.id) return [];
    return atoData.filter(row =>
      row.id !== editValues.id &&
      row.isSpare &&
      (!editValues.takeoffDate || row.takeoffDate === editValues.takeoffDate)
    );
  }, [atoData, editValues?.id, editValues?.takeoffDate]);
  
  // helper to format an ISO timestamp as "YYYY-MM-DD HH:MM:SS" in 24-hour local time
  function formatLocalTimestamp(isoString) {
    const dt = new Date(isoString);
    const pad = n => String(n).padStart(2, '0');
    const Y = dt.getFullYear();
    const M = pad(dt.getMonth() + 1);
    const D = pad(dt.getDate());
    const h = pad(dt.getHours());
    const m = pad(dt.getMinutes());
    const s = pad(dt.getSeconds());
    return `${Y}-${M}-${D} ${h}:${m}`; //:${s} to add back seconds
  }

  // tell the ato table which rows have open issues
  const openAtoIds = new Set(
    issues
      .filter(issue => !issue.fixed && issue.atoId != null)
      .map(issue => String(issue.atoId))
  );

  const getLaunchPersonnelText = row =>
    (row.launchPersonnel || []).map(empNum => {
      const person = personnelData.find(
        p => String(p['Emp Num']) === String(empNum)
      );

      return person
        ? `${person['Last Name']}, ${person['First Name']} (${person['Shop Name']})`
        : `${empNum}`;
    }).join(' | ');

  const getRecoveryPersonnelText = row =>
    (row.recoveryPersonnel || []).map(empNum => {
      const person = personnelData.find(
        p => String(p['Emp Num']) === String(empNum)
      );

      return person
        ? `${person['Last Name']}, ${person['First Name']} (${person['Shop Name']})`
        : `${empNum}`;
    }).join(' | ');

  const renderPersonnelList = (empNums = []) => (
    empNums.map(empNum => {
      const person = personnelData.find(
        p => String(p['Emp Num']) === String(empNum)
      );
      const label = person
        ? `${person['Last Name']}, ${person['First Name']} (${person['Shop Name']})`
        : `⚠ ${empNum}`;
      return <div key={empNum}>{label}</div>;
    })
  );

  const openLaunchPredictionDetails = (row, prediction, event) => {
    event?.stopPropagation();
    if (!prediction) return;
    setSelectedLaunchPrediction({
      ...prediction,
      row,
    });
    setLaunchFeedback({
      accepted: prediction.accepted == null ? '' : String(Boolean(prediction.accepted)),
      feedbackAction: prediction.feedbackAction || '',
      feedbackReason: prediction.feedbackReason || '',
    });
  };

  const submitLaunchPredictionFeedback = async () => {
    if (!selectedLaunchPrediction?.id || launchFeedback.accepted === '') {
      setNotification('Select accept or reject before saving feedback.');
      setTimeout(() => setNotification(''), 3000);
      return;
    }
    try {
      const res = await api.post('/launch-success-prediction-feedback', {
        email: user.email,
        location: selectedBase,
        predictionId: selectedLaunchPrediction.id,
        accepted: launchFeedback.accepted === 'true',
        feedbackAction: launchFeedback.feedbackAction,
        feedbackReason: launchFeedback.feedbackReason,
      });
      const updated = res.data.prediction;
      setSelectedLaunchPrediction(prev => ({ ...prev, ...updated }));
      setLaunchPredictions(prev => prev.map(item => item.id === updated.id ? updated : item));
      setNotification('Launch prediction feedback saved.');
      setTimeout(() => setNotification(''), 3000);
    } catch (err) {
      setNotification(err.response?.data?.message || 'Unable to save launch prediction feedback.');
      setTimeout(() => setNotification(''), 4000);
    }
  };

  const submitWeatherOverride = async (overrideFactor, reason) => {
    if (!selectedLaunchPrediction?.id) return;
    try {
      setIsWeatherOverrideSaving(true);
      const res = await api.post('/launch-success-weather-override', {
        email: user.email,
        location: selectedBase,
        predictionId: selectedLaunchPrediction.id,
        overrideFactor,
        reason,
      });
      const updated = res.data.prediction;
      setSelectedLaunchPrediction(prev => ({ ...prev, ...updated, row: prev?.row }));
      setLaunchPredictions(prev => prev.map(item => item.id === updated.id ? updated : item));
      setNotification('Weather override saved.');
      setTimeout(() => setNotification(''), 3000);
    } catch (err) {
      setNotification(err.response?.data?.message || 'Unable to save weather override.');
      setTimeout(() => setNotification(''), 4000);
    } finally {
      setIsWeatherOverrideSaving(false);
    }
  };

  const getAtoCellValue = (row, key) => {
    if (key === 'lastUploadedAt') {
      return row.lastUploadedAt ? formatLocalTimestamp(row.lastUploadedAt) : '';
    }

    if (key === 'launchPersonnel') {
      return getLaunchPersonnelText(row);
    }

    if (key === 'recoveryPersonnel') {
      return getRecoveryPersonnelText(row);
    }

    if (key === 'isSpare') {
      return row.isSpare ? 'Yes' : 'No';
    }

    return row[key];
  };

  const handleSort = key => {
    setSortConfig(current => getNextSortConfig(current, key));
  };

  const handleFilterChange = (key, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const clearFilter = key => {
    setColumnFilters(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const clearAllFilters = () => {
    setColumnFilters({});
  };

  const handleInlineAtoCellSave = (row, colKey, newVal) => {
    if (!canManageFlySchedule) {
      setNotification('You have read-only access to the Fly Schedule.');
      setTimeout(() => setNotification(''), 3000);
      return;
    }
    if (colKey === 'actLand' && newVal && !row.actTakeoff) {
      setNotification('Enter Actual Takeoff before entering Actual Land.');
      setTimeout(() => setNotification(''), 4000);
      return;
    }
    const editedCellKey = `${row.id}|${colKey}`;

    setAtoData(prev =>
      prev.map(r =>
        r.id === row.id ? { ...r, [colKey]: newVal } : r
      )
    );

    setEditedCells(prev => new Set(prev).add(editedCellKey));

    if (row.id) {
      api.post('/update-ato-entry', {
        email: user.email,
        location: selectedBase,
        id: row.id,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        [colKey]: newVal
      })
        .then(res => {
          refreshAtoAndLaunchPredictions();
          if (colKey === 'landingCode' && String(newVal) === '3') {
            setNotification('Landing Code 3 saved. Review the tail\'s Current Aircraft Status in flight details or on the Status tab.');
            setTimeout(() => setNotification(''), 5000);
          }
          const returnedAlerts = res.data.alerts ?? (res.data.alert ? [res.data.alert] : []);
          returnedAlerts.forEach(a => {
            setAlerts(prev => [
              {
                id: a.id,
                message: formatAtoInlineAlertMessage(a, row, formatLocalTimestamp)
              },
              ...prev
            ]);
          });
        })
        .catch(err => {
          console.error(err);
          setNotification(err.response?.data?.message || 'Error saving changes');
          setTimeout(() => setNotification(''), 3000);
          setEditedCells(prev => {
            const next = new Set(prev);
            next.delete(editedCellKey);
            return next;
          });
          refreshAtoData();
        });
      return;
    }

    api.post('/add-ato-entry', {
      email: user.email,
      location: selectedBase,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      record: { ...row, [colKey]: newVal },
    })
      .then(() => refreshAtoAndLaunchPredictions())
      .catch(err => {
        console.error(err);
        setNotification(err.response?.data?.message || 'Error saving changes');
        setTimeout(() => setNotification(''), 3000);
        setEditedCells(prev => {
          const next = new Set(prev);
          next.delete(editedCellKey);
          return next;
        });
        refreshAtoData();
      });
  };

  const handleScheduleOffsetSave = (row, scheduleKey, nextOffset) => {
    if (!canManageFlySchedule) {
      setNotification('You have read-only access to the Fly Schedule.');
      setTimeout(() => setNotification(''), 3000);
      return;
    }
    const offsetKey = scheduleOffsetKeyMap[scheduleKey];
    const validation = validateScheduledOffsetValue(nextOffset);
    if (!validation.isValid || !offsetKey) return;

    setAtoData(prev =>
      prev.map(r =>
        r.id === row.id ? { ...r, [offsetKey]: validation.value } : r
      )
    );

    api.post('/update-ato-entry', {
      email: user.email,
      location: selectedBase,
      id: row.id,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      [offsetKey]: validation.value,
    })
      .then(() => refreshAtoAndLaunchPredictions())
      .catch(err => {
        console.error(err);
        setNotification(err.response?.data?.message || 'Error saving offset');
        setTimeout(() => setNotification(''), 3000);
        refreshAtoData();
      });
  };

  const handleSubmitSpareSwap = async () => {
    if (!editValues.id || !swapSelectedSpareId) {
      setNotification('Select a spare aircraft before swapping.');
      setTimeout(() => setNotification(''), 3000);
      return;
    }

    try {
      const res = await api.post('/swap-ato-spare', {
        email: user.email,
        location: selectedBase,
        targetId: editValues.id,
        spareId: swapSelectedSpareId,
        reason: swapReason,
      });

      (res.data.alerts || []).forEach(alert => {
        setAlerts(prev => [
          {
            id: alert.id,
            message: `Spare swap: ${alert.old_value || editValues.tail} -> ${alert.new_value || alert.tail}`,
            details: alert.details,
          },
          ...prev,
        ]);
      });

      setSwapSelectedSpareId('');
      setSwapReason('');
      setEditModalOpen(false);
      setNotification(res.data.message || 'Spare aircraft swapped into mission.');
      await refreshAtoAndLaunchPredictions();
      setTimeout(() => setNotification(''), 4000);
    } catch (err) {
      console.error('Spare swap failed:', err);
      setNotification(err.response?.data?.message || 'Unable to swap spare aircraft');
      setTimeout(() => setNotification(''), 4000);
    }
  };

  const handleTimeRangeFilterChange = (key, bound, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [key]: {
        from: prev[key]?.from || '',
        to: prev[key]?.to || '',
        [bound]: value,
      },
    }));
  };

  const matchesAtoFilter = (row, key) => {
    if (key === 'takeoffDate') {
      return matchesDateFilter(row.takeoffDate, columnFilters.takeoffDate);
    }

    if (key === 'lastUploadedAt') {
      return matchesDateFilter(row.lastUploadedAt, columnFilters.lastUploadedAt);
    }

    if (scheduleColumnKeys.includes(key)) {
      return matchesTimeRangeFilter(row[key], columnFilters[key] || {});
    }

    if (key === 'uploadedBy') {
      return matchesSelectFilter(row.uploadedBy, columnFilters.uploadedBy);
    }

    return matchesColumnFilter(getAtoCellValue(row, key), columnFilters[key]);
  };

  const renderAtoFilterCell = key => {
    if (key === 'takeoffDate') {
      return (
        <ClearableInput
          type="date"
          value={columnFilters.takeoffDate || ''}
          onChange={e => handleFilterChange('takeoffDate', e.target.value)}
        />
      );
    }

    if (key === 'lastUploadedAt') {
      return (
        <ClearableInput
          type="date"
          value={columnFilters.lastUploadedAt || ''}
          onChange={e => handleFilterChange('lastUploadedAt', e.target.value)}
        />
      );
    }

    if (scheduleColumnKeys.includes(key)) {
      const range = columnFilters[key] || { from: '', to: '' };

      return (
        <ClearableRangeInputs
          type="time"
          from={range.from}
          to={range.to}
          onFromChange={e => handleTimeRangeFilterChange(key, 'from', e.target.value)}
          onToChange={e => handleTimeRangeFilterChange(key, 'to', e.target.value)}
        />
      );
    }

    if (key === 'uploadedBy') {
      return (
        <select
          value={columnFilters.uploadedBy || ''}
          onChange={e => handleFilterChange('uploadedBy', e.target.value)}
          style={{ width: '100%' }}
        >
          <option value="">All</option>
          {uploadedByOptions.map(option => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }

    if (key === 'isSpare') {
      return (
        <select
          value={columnFilters.isSpare || ''}
          onChange={e => handleFilterChange('isSpare', e.target.value)}
          style={{ width: '100%' }}
        >
          <option value="">All</option>
          <option value="Yes">Spare only</option>
          <option value="No">Not spare</option>
        </select>
      );
    }

    return (
      <ClearableInput
        type="text"
        value={columnFilters[key] || ''}
        onChange={e => handleFilterChange(key, e.target.value)}
        placeholder={`Filter ${columns.find(col => col.key === key)?.label || key}`}
      />
    );
  };

  // reusable reload ATO data function
  const refreshAtoData = async () => {
    if (!user) return;
    try {
      const res = await api.get("/get-ato", {
        params: { email: user.email, location: selectedBase, global: globalView }
      });
      setFeatureFlags(res.data.featureFlags || {});
      const sorted = [...res.data.data].sort(compareAtoDefaultSort);
      
      setAtoData(sorted);
    } catch (err) {
      console.error("Failed to load ATO data:", err);
    }
  };

  const refreshAircraftStatusRows = async () => {
    if (!user) return;
    try {
      const res = await api.get("/get-aircraft-status", {
        params: { email: user.email, location: selectedBase, global: globalView }
      });
      setAircraftStatusRows(res.data.data || []);
    } catch (err) {
      console.error("Failed to load aircraft status data:", err);
      setAircraftStatusRows([]);
    }
  };

  const loadLaunchPreset = async () => {
    if (!user?.email || !selectedBase) return;
    setLaunchPresetLoading(true);
    setLaunchPresetError('');
    try {
      const res = await api.get('/launch-schedule-preset', {
        params: {
          email: user.email,
          location: selectedBase,
        },
      });
      const preset = { ...DEFAULT_LAUNCH_PRESET, ...(res.data.preset || {}) };
      setLaunchPreset(preset);
      setLaunchPresetDraft(preset);
    } catch (err) {
      console.error('Failed to load launch presets', err);
      setLaunchPresetError(err.response?.data?.message || 'Unable to load launch presets.');
    } finally {
      setLaunchPresetLoading(false);
    }
  };

  const openLaunchPresetModal = async () => {
    if (!canManageFlySchedule) {
      setNotification('You have read-only access to the Fly Schedule.');
      setTimeout(() => setNotification(''), 3000);
      return;
    }
    setShowLaunchPresetModal(true);
    await loadLaunchPreset();
  };

  const handleLaunchPresetDraftChange = (key, value) => {
    setLaunchPresetDraft(current => ({
      ...current,
      [key]: value,
    }));
  };

  const saveLaunchPreset = async () => {
    if (!canManageFlySchedule) {
      setLaunchPresetError('You have read-only access to the Fly Schedule.');
      return;
    }
    const payload = { email: user.email, location: selectedBase };
    for (const field of LAUNCH_PRESET_FIELDS) {
      const value = Number(launchPresetDraft[field.key]);
      if (!Number.isInteger(value) || value < 0 || value > 1440) {
        setLaunchPresetError(`${field.label} must be a whole number from 0 to 1440 minutes.`);
        return;
      }
      payload[field.key] = value;
    }

    setLaunchPresetSaving(true);
    setLaunchPresetError('');
    try {
      const res = await api.post('/launch-schedule-preset', payload);
      const preset = { ...DEFAULT_LAUNCH_PRESET, ...(res.data.preset || {}) };
      setLaunchPreset(preset);
      setLaunchPresetDraft(preset);
      setNotification(res.data.message || 'Launch presets saved');
      setShowLaunchPresetModal(false);
      setTimeout(() => setNotification(''), 3000);
    } catch (err) {
      console.error('Failed to save launch presets', err);
      setLaunchPresetError(err.response?.data?.message || 'Unable to save launch presets.');
    } finally {
      setLaunchPresetSaving(false);
    }
  };

  const refreshLaunchPredictions = async () => {
    if (!user || !selectedBase) {
      setLaunchPredictions([]);
      return;
    }
    const candidateIds = atoData
      .filter(row => shouldShowAtoRowForDate(row, selectedDate, showCompleted))
      .filter(row => filterableColumns.every(col => matchesAtoFilter(row, col.key)))
      .filter(isLaunchPredictionCandidateRow)
      .map(row => row.id);

    if (candidateIds.length === 0) {
      setLaunchPredictions([]);
      return;
    }

    try {
      const res = await api.get("/launch-success-predictions", {
        params: {
          email: user.email,
          location: selectedBase,
          global: globalView,
          atoIds: candidateIds.join(','),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }
      });
      setLaunchPredictions(res.data.data || []);
    } catch (err) {
      console.error("Failed to load launch success predictions:", err);
      setLaunchPredictions([]);
    }
  };

  const refreshAtoAndLaunchPredictions = async () => {
    await refreshAtoData();
    await refreshLaunchPredictions();
  };

  const handleAutoAssignLaunch = async () => {
    if (!canManageFlySchedule) {
      setNotification('You have read-only access to the Fly Schedule.');
      setTimeout(() => setNotification(''), 3000);
      return;
    }
    try {
      setIsAutoAssigningLaunch(true);
      const res = await api.post("/auto-assign-launch", {
        email: user.email,
        location: selectedBase,
      });

      if (res.data.alerts) {
        res.data.alerts.forEach(alert => {
          setAlerts(prev => [
            {
              id: alert.id,
              message: `Warning: ${alert.details}`,
              details: alert.details,
            },
            ...prev,
          ]);
        });
      }

      await refreshAtoAndLaunchPredictions();

      const warningCount = res.data.warnings?.length || 0;
      setNotification(
        warningCount > 0
          ? `${res.data.message} ${warningCount} warning${warningCount === 1 ? '' : 's'} added to alerts.`
          : res.data.message
      );
      setTimeout(() => setNotification(''), 4000);
    } catch (err) {
      console.error("Launch auto-assign failed:", err);
      setNotification(err.response?.data?.message || "Unable to auto-assign launch personnel");
      setTimeout(() => setNotification(''), 4000);
    } finally {
      setIsAutoAssigningLaunch(false);
    }
  };

  const handleAutoAssignRecovery = async () => {
    if (!recoverySchedulingEnabled) {
      setNotification('Upgrade required to use Recovery Personnel Scheduling for this location.');
      setTimeout(() => setNotification(''), 4000);
      return;
    }
    if (!canManageFlySchedule) {
      setNotification('You have read-only access to the Fly Schedule.');
      setTimeout(() => setNotification(''), 3000);
      return;
    }
    try {
      setIsAutoAssigningRecovery(true);
      const res = await api.post("/auto-assign-recovery", {
        email: user.email,
        location: selectedBase,
      });

      if (res.data.alerts) {
        res.data.alerts.forEach(alert => {
          setAlerts(prev => [
            {
              id: alert.id,
              message: `Warning: ${alert.details}`,
              details: alert.details,
            },
            ...prev,
          ]);
        });
      }

      await refreshAtoAndLaunchPredictions();

      const warningCount = res.data.warnings?.length || 0;
      setNotification(
        warningCount > 0
          ? `${res.data.message} ${warningCount} warning${warningCount === 1 ? '' : 's'} added to alerts.`
          : res.data.message
      );
      setTimeout(() => setNotification(''), 4000);
    } catch (err) {
      console.error("Recovery auto-assign failed:", err);
      setNotification(err.response?.data?.message || "Unable to auto-assign recovery personnel");
      setTimeout(() => setNotification(''), 4000);
    } finally {
      setIsAutoAssigningRecovery(false);
    }
  };

  // for default ATO schedule template, append selected view location
  const handleUpload = async (e, type) => {
    if (!canManageFlySchedule) {
      setNotification('You have read-only access to the Fly Schedule.');
      setTimeout(() => setNotification(''), 3000);
      return;
    }
    console.log("▶︎ handleUpload called:", type, e.target.files[0]);  
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("email", user.email);
    if (type === "ato") {
      formData.append("location", selectedBase);
    }
    try {
      const res = await api.post(`/upload-${type}`, formData, {
        params: { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone },
      });
      // show success in ribbon banner
      setNotification(res.data.message);             
      // 2) Push any server-sent alerts into the bell-badge
      if (res.data.alerts) {
        res.data.alerts.forEach(alert => {
          setAlerts(prev => [
            {
              id:      alert.id,
              message: `[${alert.tail}] ${alert.action}: ${alert.new_value}`
            },
            ...prev
          ]);       
        });
      }    
      // 3) Instantly update table data from the upload response
      if (res.data.ato_data) {
        setAtoData(res.data.ato_data);
      } else {
        // fallback if server didn’t send it
        await fetchData(type, selectedBase);
      }
      // clear banner after 3s
      setTimeout(() => setNotification(""), 3000);      
      // redundant Clear input value to allow re-uploading the same file later
      defaultAtoRef.current.value = "";
    } catch (err) {
          console.error("Upload failed:", err.response?.data.message || err.message);
          alert("Server error: " + (err.response?.data.message || err.message));
    };
  };

  const handleDownloadAtoTemplate = async () => {
    try {
      const res = await api.get('/download-ato-template', {
        params: { email: user.email },
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'mfe_ato_upload_template.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Failed to download ATO template', err);
      alert(err.response?.data?.message || 'Unable to download ATO template');
    }
  };

  const onATOFileChange = e => {
    if (!canManageFlySchedule) {
      setNotification('You have read-only access to the Fly Schedule.');
      setTimeout(() => setNotification(''), 3000);
      return;
    }
    const file = e.target.files[0];
    setPendingFile(file);
    const reader = new FileReader();
    reader.onload = evt => {
      const wb = XLSX.read(evt.target.result, { type: 'binary' });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const headers = XLSX.utils.sheet_to_json(sheet, { header: 1 })[0];
      const name = wb.SheetNames[0];
      setSheetName(name);
      setExcelHeaders(headers);
      setShowMapper(true);
    };
    reader.readAsBinaryString(file);
  };

  const handleAddAtoRow = async () => {
    if (!canManageFlySchedule) {
      setNotification('You have read-only access to the Fly Schedule.');
      setTimeout(() => setNotification(''), 3000);
      return;
    }
    const emptyAto = {
      takeoffDate: '',
      lineNum: '',
      callSign: '',
      tail: '',
      priority: '',
      spot: '',
      config: '',
      acftStatus: '',
      fuelGoal: '',
      isSpare: false,
      missionType: '',
      schedMxShow: '',
      schedCrewShow: '',
      schedEngStrt: '',
      schedTaxi: '',
      schedTakeoff: '',
      schedLand: '',
      landingCode: '',
      schedMxShowOffsetDays: 0,
      schedCrewShowOffsetDays: 0,
      schedEngStrtOffsetDays: 0,
      schedTaxiOffsetDays: 0,
      schedLandOffsetDays: 0,
      launchPersonnel: [],
      recoveryPersonnel: [],
      comments: '',
      cancelled: false,
      cancelReason: '',
      uploadedBy: user.email,
      lastUploadedAt: new Date().toISOString()
    };
    setAtoData(prev => [...prev, emptyAto]);
    
    //persist to server
    try {
      await api.post("/add-ato-entry", {
        email: user.email,
        location: selectedBase,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        record: emptyAto
      });
      // 3) Re-fetch to get any server‑side defaults if you want
      refreshAtoAndLaunchPredictions();
    } catch (err) {
      console.error("Failed to save new ATO row", err);
    }
  };
  
  // open ATO modal with row data
  const handleEditClick = row => {
    if (!canManageFlySchedule) {
      setNotification('You have read-only access to the Fly Schedule.');
      setTimeout(() => setNotification(''), 3000);
      return;
    }
    const fields = ["actMxShow", "actCrewShow", "actEngStrt", "actTaxi", "actTakeoff", "actLand"];
    const values = {};
  
    fields.forEach(key => {
      values[key] = row[key] ? toLocalInputFormat(row[key]) : "";
    });
  
    const snapshot = { ...row }; // ← raw server format (includes UTC timestamps)
    const tailKey = String(row.tail || '').trim().toUpperCase();
    const desiredLocation = row.location || selectedBase;
    const statusRow =
      aircraftStatusRows.find(status =>
        String(status.tail || '').trim().toUpperCase() === tailKey &&
        (!desiredLocation || status.location === desiredLocation)
      ) ||
      aircraftStatusByTail.get(tailKey);
    setOriginalSnapshot(snapshot);   // Save unmodified row from DB
    setEditValues({
      id: row.id,
      lineNum: row.lineNum || '',
      tail: row.tail || '',
      takeoffDate: row.takeoffDate || '',
      ...values,
      launchPersonnel: row.launchPersonnel || [],
      recoveryPersonnel: row.recoveryPersonnel || [],
      recoverySpot: row.recoverySpot || '',
      confirmRecoverySpot: false,
      comments: row.comments || '',
      isSpare: Boolean(row.isSpare),
      spareSwappedIn: Boolean(row.spareSwappedIn),
      originalTail: row.originalTail || '',
      spareSwapReason: row.spareSwapReason || '',
      currentFuel: statusRow?.fuel || '',
      currentFuelOriginal: statusRow?.fuel || '',
      statusRowId: statusRow?.id || '',
      statusRowLocation: statusRow?.location || selectedBase,
      cancelled: row.cancelled || false,
      cancelReason: row.cancelReason || ''
    });
    const firstSpare = atoData.find(candidate =>
      candidate.id !== row.id &&
      candidate.isSpare &&
      (!row.takeoffDate || candidate.takeoffDate === row.takeoffDate)
    );
    setSwapSelectedSpareId(firstSpare?.id || '');
    setSwapReason('');
  
    setEditModalOpen(true);
  };


    // Save ato modal data handler
  const handleSaveEdit = async () => {
    if (!canManageFlySchedule) {
      setNotification('You have read-only access to the Fly Schedule.');
      setTimeout(() => setNotification(''), 3000);
      setEditModalOpen(false);
      return;
    }
    const payload = {
      email: user.email,
      location: selectedBase,
      id: editValues.id,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
  
    const editableKeys = [
      'actMxShow', 'actCrewShow', 'actEngStrt',
      'actTaxi', 'actTakeoff', 'actLand',
      'launchPersonnel', 'recoveryPersonnel', 'comments', 'isSpare',
      'recoverySpot',
      'cancelled', 'cancelReason'
    ];
  
    const timeFields = [
      'actMxShow', 'actCrewShow', 'actEngStrt',
      'actTaxi', 'actTakeoff', 'actLand'
    ];

    if (editValues.actLand && !editValues.actTakeoff && !originalSnapshot.actTakeoff) {
      setNotification('Enter Actual Takeoff before entering Actual Land.');
      setTimeout(() => setNotification(''), 4000);
      return;
    }

    if (editValues.confirmRecoverySpot) {
      payload.confirmRecoverySpot = true;
    }
  
    for (const key of editableKeys) {
      let newVal = editValues[key];
  
      if (timeFields.includes(key)) {
        newVal = newVal ? toUTCISOString(newVal) : null; // convert to UTC for comparison, or clear field
      }
  
      let oldVal = originalSnapshot[key];
      let changed = false;
  
      if (timeFields.includes(key)) {
        changed = normalizeIsoMinute(newVal) !== normalizeIsoMinute(oldVal);
      } else {
        // normalize Date comparison
        if (oldVal instanceof Date) oldVal = oldVal.toISOString();
        if (oldVal && typeof oldVal === "string" && oldVal.endsWith("Z")) {
          oldVal = oldVal.replace(".000Z", "Z"); // strip milliseconds for cleaner match
        }
        changed = JSON.stringify(newVal) !== JSON.stringify(oldVal);
      }

      if (changed) {
        payload[key] = newVal;
      }
    }
  
    const currentFuelChanged =
      Boolean(editValues.statusRowId) &&
      String(editValues.currentFuel ?? '') !== String(editValues.currentFuelOriginal ?? '');
    if (Object.keys(payload).length <= 4 && !currentFuelChanged) {
      setEditModalOpen(false);
      setNotification("No changes made");
      setTimeout(() => setNotification(""), 2000);
      return;
    }
  
    try {
      let returnedAlerts = [];

      if (Object.keys(payload).length > 4) {
        console.log("🧪 payload:", JSON.stringify(payload, null, 2));
        const res = await api.post("/update-ato-entry", payload);
        returnedAlerts = res.data.alerts ?? [];
      }

      if (currentFuelChanged) {
        const fuelRes = await api.post("/update-aircraft-status-entry", {
          email: user.email,
          location: editValues.statusRowLocation || selectedBase,
          id: editValues.statusRowId,
          field: 'fuel',
          value: editValues.currentFuel,
        });
        if (fuelRes.data.alert) {
          returnedAlerts.push(fuelRes.data.alert);
        }
        await refreshAircraftStatusRows();
      }

      const spareJustEnabled = payload.isSpare === true && originalSnapshot.isSpare !== true;
  
      // show only 1 summary alert
      if (returnedAlerts.length > 0) {
        const summary = returnedAlerts
          .map(a => {
            const isTime = timeFields.includes(a.field) || [
              'act_mx_show', 'act_crew_show', 'act_eng_strt',
              'act_taxi', 'act_takeoff', 'act_land'
            ].includes(a.field);
            let val = a.new_value;
            if (isTime && val) val = formatLocalTimestamp(val).split(" ")[1];
            return `${a.field}: ${val}`;
          })
          .join(", ");
  
        const alert = {
          id: returnedAlerts[0].id,
          message: `[${returnedAlerts[0].tail}] Updated: ${summary}`
        };
        setAlerts(prev => [alert, ...prev]);
      }
  
      setEditModalOpen(false);
      setNotification(spareJustEnabled ? "Spare saved and scheduled times cleared" : "Changes saved successfully");
      await refreshAtoAndLaunchPredictions();
      setTimeout(() => setNotification(""), 3000);
    } catch (err) {
      console.error("❌ save error:", err);
      setNotification("Error saving changes");
      setTimeout(() => setNotification(""), 3000);
    }
  };
    
  // include today’s flights + any prior-day flights still incomplete, with a hide-completed toggle
  // with special rules for cancelled rows
  const flightsToRender = atoData.filter(r => shouldShowAtoRowForDate(r, selectedDate, showCompleted));

  const displayedFlights = flightsToRender
    .filter(row =>
      filterableColumns.every(col =>
        matchesAtoFilter(row, col.key)
      )
    )
    .slice()
    .sort((left, right) => {
      if (!sortConfig) return compareAtoDefaultSort(left, right);

      return compareTableValues(
        getAtoCellValue(left, sortConfig.key),
        getAtoCellValue(right, sortConfig.key),
        sortConfig.direction
      );
    });

  const hasActiveColumnFilters = Object.values(columnFilters).some(value => {
    if (value == null) return false;
    if (typeof value === 'object') {
      return Object.values(value).some(Boolean);
    }
    return Boolean(value);
  });

  const filterLabelMap = Object.fromEntries([
    ...columns.map(col => [col.key, col.label]),
  ]);

  const activeFilterChips = Object.entries(columnFilters).flatMap(([key, value]) => {
    if (!value) return [];

    if (typeof value === 'object') {
      const from = value.from || '';
      const to = value.to || '';
      if (!from && !to) return [];
      const descriptor = from && to
        ? `${from} to ${to}`
        : from
        ? `from ${from}`
        : `to ${to}`;

      return [{
        key,
        label: `${filterLabelMap[key] || key}: ${descriptor}`,
      }];
    }

    return [{
      key,
      label: `${filterLabelMap[key] || key}: ${value}`,
    }];
  });

 
  const handleDelete = async (row, e) => {
    // 1) Prevent row-select
    e.stopPropagation();
    if (!canManageFlySchedule) {
      setNotification('You have read-only access to the Fly Schedule.');
      setTimeout(() => setNotification(''), 3000);
      return;
    }

    // 2) Confirm
    if (!window.confirm("Delete this ATO row?")) return;

    try {
      // 3) Call the delete endpoint
      const res = await api.post("/delete-ato-entry", {
        email:    user.email,
        location: selectedBase,
        id:       row.id
      });

      // 4) If the server returned an alert, push it into your bell-icon and changelog
      if (res.data.alert) {
        const { id, tail, takeoffDate, action, details } = res.data.alert;
        const dateOnly = formatLocalTimestamp(takeoffDate).split(" ")[0];

        setAlerts(prev => [
          {
            id,
            tail,
            action,
            details,
            takeoffDate,
            message: `Deleted tail: [${tail} | ${dateOnly}]`
          },
          ...prev
        ]);
      }

      // 5) Refresh the table
      await refreshAtoAndLaunchPredictions();
    } catch (err) {
      console.error("Failed to delete ATO row", err);
      setNotification(err.response?.data?.message || "Unable to delete this Fly Schedule row.");
      setTimeout(() => setNotification(''), 5000);
    }
  };
  
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNowTick(Date.now());
    }, 60000);

    return () => window.clearInterval(intervalId);
  }, []);

  // load a stored mapping  
  useEffect(() => {
    if (!sheetName) return;
    const stored = localStorage.getItem(`ato-mapping-${sheetName}`);
    if (stored) setMapping(JSON.parse(stored));
  }, [sheetName]);   

  useEffect(() => {
    setColumns(mergeAtoColumns(tableSettings?.columns));
    setCollapseSettings(mergeAtoCollapseSettings(tableSettings?.collapse, tableSettings?.columns));
  }, [tableSettings]);

  
  useEffect(() => {
    refreshAtoData();
    refreshAircraftStatusRows();
  }, [user, selectedBase, globalView]);

  useEffect(() => {
    loadLaunchPreset();
  }, [user, selectedBase]);

  useEffect(() => {
    refreshLaunchPredictions();
  }, [user, selectedBase, globalView, selectedDate, showCompleted, atoData.length, issues.length, columnFilters, columns, launchSuccessAdvisorEnabled]);

  useEffect(() => {
    const shell = tableShellRef.current;
    const stickyScrollbar = stickyScrollbarRef.current;
    const stickyScrollbarInner = stickyScrollbarInnerRef.current;

    if (!shell || !stickyScrollbar || !stickyScrollbarInner) return undefined;

    let isSyncing = false;
    let observedTable = shell.querySelector('table');

    const syncMeasurements = () => {
      observedTable = shell.querySelector('table');
      const scrollWidth = observedTable ? observedTable.scrollWidth : shell.scrollWidth;

      stickyScrollbarInner.style.width = `${scrollWidth}px`;
      setShowStickyScrollbar(scrollWidth > shell.clientWidth + 1);

      if (!isSyncing) {
        stickyScrollbar.scrollLeft = shell.scrollLeft;
      }
    };

    const syncFromShell = () => {
      if (isSyncing) return;
      isSyncing = true;
      stickyScrollbar.scrollLeft = shell.scrollLeft;
      isSyncing = false;
    };

    const syncFromStickyScrollbar = () => {
      if (isSyncing) return;
      isSyncing = true;
      shell.scrollLeft = stickyScrollbar.scrollLeft;
      isSyncing = false;
    };

    let frameId = null;
    const scheduleMeasurements = () => {
      if (frameId !== null) return;
      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        syncMeasurements();
      });
    };

    syncMeasurements();

    shell.addEventListener('scroll', syncFromShell, { passive: true });
    stickyScrollbar.addEventListener('scroll', syncFromStickyScrollbar, { passive: true });
    window.addEventListener('resize', scheduleMeasurements);

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => {
          scheduleMeasurements();
        })
      : null;

    if (resizeObserver) {
      resizeObserver.observe(shell);
      if (observedTable) resizeObserver.observe(observedTable);
    }

    return () => {
      shell.removeEventListener('scroll', syncFromShell);
      stickyScrollbar.removeEventListener('scroll', syncFromStickyScrollbar);
      window.removeEventListener('resize', scheduleMeasurements);
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      resizeObserver?.disconnect();
    };
  }, [visibleColumns.length, atoData.length, personnelData.length, displayedFlights.length]);
  
  return (
    <div className="ato-screen">
      <div className="ato-toolbar">
        <div className="ato-toolbar__zone ato-toolbar__zone--left">
          <div className="ato-toolbar__date-group">
            <label htmlFor="datePicker" className="ato-toolbar__label">Select Date:</label>
            <input
              id="datePicker"
              type="date"
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
            />
          </div>
          <div className="ato-toolbar__status-group">
            <label className="ato-toolbar__toggle">
              <input
                type="checkbox"
                checked={showCompleted}
                onChange={e => setShowCompleted(e.target.checked)}
              />
              <span>Show Completed Flights</span>
            </label>
            <div className="ato-toolbar__legend">
              <div className="ato-toolbar__legend-swatch" />
              <span>Completed flight</span>
            </div>
          </div>
          <div className="ato-toolbar__collapse-group">
            <button
              type="button"
              className="ato-toolbar__collapse-customize"
              onClick={() => setShowCollapseSettings(true)}
            >
              Customize Collapse
            </button>
            <button
              type="button"
              className="ato-toolbar__collapse-toggle"
              onClick={() => handleCollapseSettingsChange(prev => ({ ...prev, collapsed: !prev.collapsed }))}
            >
              {collapseSettings.collapsed ? 'Expand Table' : 'Collapse Table'}
            </button>
          </div>
        </div>

        <div className="ato-toolbar__zone ato-toolbar__zone--center">
        {user && canManageFlySchedule && (
          <>
            <input
              ref={defaultAtoRef}
              type="file"
              accept=".xlsx"
              style={{ display: "none" }}
              onClick={e => (e.target.value = null)}
              onChange={e => handleUpload(e, "ato")}
            />
            <div className="ato-toolbar__primary-actions">
              <button className="ato-toolbar__add-row" onClick={handleAddAtoRow}>+ Add ATO Row</button>
              <button className="personnel-upload-button ato-upload-button" onClick={() => defaultAtoRef.current.click()}>
                Upload ATO Template
              </button>
              <button type="button" className="ato-toolbar__download-template" onClick={handleDownloadAtoTemplate}>
                Download ATO Template
              </button>
              <button type="button" className="ato-toolbar__launch-presets" onClick={openLaunchPresetModal}>
                Launch Presets
              </button>
              <button className="personnel-upload-button ato-upload-button" onClick={() => customAtoRef.current.click()}>
                Upload Custom ATO File
              </button>
            </div>
    
            <input
              ref={customAtoRef}
              type="file"
              accept=".xlsx"
              style={{ display: "none" }}
              onClick={e => (e.target.value = null)}
              onChange={onATOFileChange}
            />
    
          </>
        )}
      </div>

      <div className="ato-toolbar__zone ato-toolbar__zone--right">
        <div className="ato-toolbar__filters">
          <FilterToolbar
            chips={activeFilterChips}
            onRemoveChip={clearFilter}
            onClearAll={clearAllFilters}
          />
        </div>
        {user && (
          <button
            type="button"
            className="status-field-settings-button ato-field-settings-button"
            onClick={() => setShowSettings(true)}
          >
            Field Settings
          </button>
        )}
        <details className="ato-toolbar__help">
          <summary>Milestone Status ?</summary>
          <div className="ato-toolbar__help-popover">
            <div className="ato-toolbar__help-title">Milestone status</div>
            <div className="ato-toolbar__milestone-legend-row">
              <span className="ato-milestone-legend-sample is-complete is-on-time">
                <span className="ato-milestone-legend-icon">Done</span>
              </span>
              <span>Solid fill = actual entered</span>
            </div>
            <div className="ato-toolbar__milestone-legend-row">
              <span className="ato-milestone-legend-sample is-pending is-warning">
                <span className="ato-milestone-legend-icon">Due</span>
              </span>
              <span>Striped fill = overdue, actual blank</span>
            </div>
            <div className="ato-toolbar__milestone-legend-scale">
              Green: on time to 1 min late • Gold: over 1 to 15 min late • Red: over 15 min late
            </div>
          </div>
        </details>
      </div>
      </div>

      {/* mapping modal */}
      {showMapper && (
        <ColumnMapperModal
          excelHeaders={excelHeaders}
          defaultCols={initialColumns
             .filter(c => !['landingCode', 'uploadedBy', 'lastUploadedAt'].includes(c.key))}
          onSave={mapping => {
            if (!canManageFlySchedule) {
              setNotification('You have read-only access to the Fly Schedule.');
              setTimeout(() => setNotification(''), 3000);
              setShowMapper(false);
              return;
            }
            const form = new FormData();
            form.append('file', pendingFile);
            form.append('email', user.email);
            form.append('location', selectedBase);
            form.append('mapping', JSON.stringify(mapping));
            
            api.post('/upload-ato-custom', form, {
              params: { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone },
            })
              .then(res => {
                refreshAtoAndLaunchPredictions();
                
                if (res.data.alerts) {
                  res.data.alerts.forEach(alert => {
                    setAlerts(prev => [
                      {
                        id: alert.id,
                        message: `[${alert.tail}] ${alert.action}: ${alert.new_value}`
                      },
                      ...prev
                    ]);
                  });
                }
                
                // Persist the mapping locally so you don’t have to remap next time
                localStorage.setItem(
                  `ato-mapping-${sheetName}`,
                  JSON.stringify(mapping)
                );
                customAtoRef.current.value = "";
                setShowMapper(false);
              })
              .catch(err => {
                console.error("Upload failed:", err);
                alert("Upload failed: " + (err.response?.data?.message || err.message));
              });
          }}
          onCancel={() => setShowMapper(false)}
          api={api}
          user={user}
          selectedBase={selectedBase}
          sheetName={sheetName}
       />
      )}

      {/* column settings */}
      {showSettings && (
        <ColumnSettings
          columns={columns}
          setColumns={handleColumnsChange}
          onClose={() => setShowSettings(false)}
        />
      )}

      {showCollapseSettings && (
        <CollapseSettings
          columns={columns}
          hiddenKeys={collapseSettings.hiddenKeys}
          setHiddenKeys={hiddenKeysOrUpdater => handleCollapseSettingsChange(prev => ({
            ...prev,
            hiddenKeys: typeof hiddenKeysOrUpdater === 'function'
              ? hiddenKeysOrUpdater(prev.hiddenKeys)
              : hiddenKeysOrUpdater,
          }))}
          onClose={() => setShowCollapseSettings(false)}
        />
      )}

      {/* ATO table */}
      <div className="table-stack">
      <div
        ref={stickyScrollbarRef}
        className={`sticky-scrollbar ${showStickyScrollbar ? '' : 'is-hidden'}`}
      >
        <div ref={stickyScrollbarInnerRef} className="sticky-scrollbar__inner" />
      </div>
      <div className="table-shell" ref={tableShellRef}>
      <table
        border="1"
        cellPadding="5"
        cellSpacing="0"
        className={`compact-table ato-schedule-table ${collapseSettings.collapsed ? 'is-collapsed' : ''}`}
      >
          <thead>
            <tr>
              <th />
              {visibleColumns.map(col => (
                col.key === 'launchPersonnel' ? (
                <th key={col.key} className="ato-launch-col">
                  <div className="ato-launch-header">
                    {canManageFlySchedule ? (
                      <button
                        type="button"
                        className="ato-launch-header__assign"
                        data-tour-target="auto-assign-launch"
                        onClick={handleAutoAssignLaunch}
                        disabled={isAutoAssigningLaunch}
                      >
                        {isAutoAssigningLaunch ? 'Assigning...' : 'Auto-Assign Launch'}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => handleSort('launchPersonnel')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}
                    >
                      Launch Personnel{getSortIndicator(sortConfig, 'launchPersonnel')}
                    </button>
                  </div>
                </th>
                ) : col.key === 'recoveryPersonnel' ? (
                <th key={col.key} className={`ato-recovery-col ${recoverySchedulingEnabled ? '' : 'is-premium-locked'}`}>
                  <div className="ato-launch-header">
                    {canManageFlySchedule ? (
                      <button
                        type="button"
                        className="ato-launch-header__assign"
                        onClick={handleAutoAssignRecovery}
                        disabled={isAutoAssigningRecovery}
                        title={recoverySchedulingEnabled ? 'Auto-assign recovery personnel' : 'Upgrade required to use Recovery Personnel Scheduling'}
                      >
                        {isAutoAssigningRecovery
                          ? 'Assigning...'
                          : recoverySchedulingEnabled
                          ? 'Auto-Assign Recovery'
                          : 'Upgrade Recovery'}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => handleSort('recoveryPersonnel')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}
                      title={recoverySchedulingEnabled ? '' : 'Premium feature. Upgrade required to schedule recovery personnel.'}
                    >
                      Recovery Personnel{getSortIndicator(sortConfig, 'recoveryPersonnel')}
                    </button>
                  </div>
                </th>
                ) : (
                <th key={col.key} className={getAtoColumnClassName(col.key)}>
                  <button
                    type="button"
                    onClick={() => handleSort(col.key)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}
                  >
                    {col.label}{getSortIndicator(sortConfig, col.key)}
                  </button>
                </th>
                )
              ))}
              <th>Actions</th>
            </tr>
            <tr>
              <th />
              {visibleColumns.map(col => (
                <th
                  key={`${col.key}-filter`}
                  className={[
                    getAtoColumnClassName(col.key),
                    col.key === 'launchPersonnel' ? 'ato-launch-col' : '',
                    col.key === 'recoveryPersonnel' ? 'ato-recovery-col' : '',
                    col.key === 'recoveryPersonnel' && !recoverySchedulingEnabled ? 'is-premium-locked' : '',
                  ].filter(Boolean).join(' ')}
                >
                  {renderAtoFilterCell(col.key)}
                </th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {displayedFlights.length > 0 ? displayedFlights.map(row => (
              <tr
                key={row.id}
                onClick={() =>
                  setSelectedAtoRow(prev => (prev === row ? null : row))
                }
                className={[
                  openAtoIds.has(String(row.id)) ? 'open-issue-border' : '',
                  row.isSpare ? 'ato-spare-row' : '',
                  row.spareStatus === 'consumed' ? 'ato-spare-consumed-row' : '',
                  row.spareSwappedIn ? 'ato-spare-swapped-row' : '',
                  row.cancelled ? 'ato-row-cancelled' : '',
                  row.actLand ? 'ato-row-landed' : '',
                  selectedAtoRow?.id === row.id ? 'ato-row-selected' : '',
                ].filter(Boolean).join(' ')}
                style={{
                  cursor: "pointer",
                  background: row.cancelled
                    ? "#333"
                    : row.actLand
                    ? "#add8e6"
                    : selectedAtoRow?.id === row.id
                    ? "#eef"
                    : "transparent",
                  color: row.cancelled ? "#fff" : "inherit",
                }}
              >
                {/* radio-button row selector */}
                <td style={{ textAlign: "center" }}>
                  <input
                    type="radio"
                    name="selectedAtoRow"
                    checked={selectedAtoRow?.id === row.id}
                    onChange={() => setSelectedAtoRow(row)}
                    onClick={e => {
                      e.stopPropagation();
                      setSelectedAtoRow(prev =>
                        prev?.id === row.id ? null : row
                      );
                    }}    
                  />
                </td>
                
                {visibleColumns.map(col => {
                  const editedCellKey = `${row.id}|${col.key}`;
                  const isScheduleCell = scheduleColumnKeySet.has(col.key);
                  const scheduleOffsetKey = scheduleOffsetKeyMap[col.key];
                  const hasScheduleOffset = Boolean(scheduleOffsetKey);
                  const milestoneStatus = milestoneScheduleKeySet.has(col.key)
                    ? getAtoMilestoneStatus(row, col.key, nowTick)
                    : null;
                  const cellClassName = [
                    isScheduleCell ? 'ato-schedule-cell' : '',
                    milestoneStatus ? 'ato-milestone-cell' : '',
                    milestoneStatus ? `is-${milestoneStatus.mode}` : '',
                    milestoneStatus ? `is-${milestoneStatus.tone}` : '',
                    col.key === 'acftStatus' ? `ato-status-tone--${getAtoStatusTone(row.acftStatus)}` : '',
                    col.key === 'acftStatus' && row.statusIsHistorical ? 'is-historical-snapshot' : '',
                    col.key === 'spot' && row.spotIsHistorical ? 'is-historical-snapshot' : '',
                  ].filter(Boolean).join(' ');
                  const cellTitle = col.key === 'acftStatus' && row.statusIsHistorical
                    ? `Status at Actual Takeoff${row.statusSnapshotAt ? ` (${formatLocalTimestamp(row.statusSnapshotAt)})` : ''}`
                    : col.key === 'spot' && row.spotIsHistorical
                      ? `Historical launch parking${row.spotSnapshotAt ? ` (${formatLocalTimestamp(row.spotSnapshotAt)})` : ''}. Edit only to correct this sortie's record.`
                      : getAtoMilestoneStatusTitle(milestoneStatus);

                  return col.key === "lastUploadedAt" ? (
                    <td key={col.key} style={{ whiteSpace: "nowrap" }}>
                      {row.lastUploadedAt &&
                        formatLocalTimestamp(row.lastUploadedAt)}
                    </td>
                  ) : (
                    col.key === 'launchPersonnel' ? (
                    <td key={col.key} className={`ato-launch-col ${row.actTakeoff ? 'is-personnel-released' : ''}`}>
                      {renderPersonnelList(row.launchPersonnel || [])}
                      {row.actTakeoff && (row.launchPersonnel || []).length > 0 ? (
                        <span className="ato-personnel-release-badge">Released</span>
                      ) : null}
                    </td>
                    ) : col.key === 'recoveryPersonnel' ? (
                    <td key={col.key} className={`ato-recovery-col ${recoverySchedulingEnabled ? '' : 'is-premium-locked'}`}>
                      {recoverySchedulingEnabled ? renderPersonnelList(row.recoveryPersonnel || []) : (
                        <button
                          type="button"
                          className="ato-premium-cell-button"
                          onClick={e => {
                            e.stopPropagation();
                            setNotification('Upgrade required to use Recovery Personnel Scheduling for this location.');
                            setTimeout(() => setNotification(''), 4000);
                          }}
                        >
                          Upgrade to schedule
                        </button>
                      )}
                    </td>
                    ) : (
                    <td
                      key={col.key}
                      className={[
                        getAtoColumnClassName(col.key),
                        cellClassName,
                      ].filter(Boolean).join(' ')}
                      title={cellTitle}
                      style={{
                        color: editedCells.has(editedCellKey)
                          ? "red"
                          : "inherit",
                      }}
                      >
                      {col.key === 'tail' ? (
                        <div className="ato-tail-cell">
                          <TailPickerCell
                            value={row[col.key]}
                            options={statusTailOptions}
                            onSave={newVal => handleInlineAtoCellSave(row, col.key, newVal)}
                            readOnly={!canManageFlySchedule}
                          />
                          {row.spareSwappedIn ? (
                            <span
                              className="ato-spare-swap-badge"
                              title={`Originally planned tail ${row.originalTail || '--'}. ${row.spareSwapReason || ''}`}
                            >
                              Spare for {row.originalTail || 'original tail'}
                            </span>
                          ) : null}
                          {row.spareStatus === 'consumed' ? (
                            <span
                              className="ato-spare-consumed-badge"
                              title={`Spare consumed by line ${row.spareConsumedLineNum || '--'}`}
                            >
                              Consumed by Line {row.spareConsumedLineNum || '--'}
                            </span>
                          ) : null}
                        </div>
                      ) : isScheduleCell ? (
                        <>
                          {hasScheduleOffset && canManageFlySchedule ? (
                            <button
                              type="button"
                              className="ato-schedule-offset-badge"
                              title="Schedule day offset relative to takeoff date. Click to cycle 0, +1, +2, -2, -1. Right-click cycles backward."
                              onClick={e => {
                                e.stopPropagation();
                                const currentOffset = Number(row[scheduleOffsetKey] || 0);
                                const nextOffset = e.shiftKey
                                  ? getPreviousScheduleOffsetValue(currentOffset)
                                  : getNextScheduleOffsetValue(currentOffset);
                                if (nextOffset !== currentOffset) {
                                  handleScheduleOffsetSave(row, col.key, nextOffset);
                                }
                              }}
                              onContextMenu={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                const currentOffset = Number(row[scheduleOffsetKey] || 0);
                                const nextOffset = getPreviousScheduleOffsetValue(currentOffset);
                                if (nextOffset !== currentOffset) {
                                  handleScheduleOffsetSave(row, col.key, nextOffset);
                                }
                              }}
                            >
                              {getScheduleOffsetBadgeLabel(row[scheduleOffsetKey])}
                            </button>
                          ) : null}
                          <div className="ato-schedule-cell__editor">
                            <EditableCell
                              value={row[col.key]}
                              type="time"
                              inputProps={{ step: 60 }}
                              validateAndNormalize={validateScheduledTimeValue}
                              onSave={newVal => handleInlineAtoCellSave(row, col.key, newVal)}
                              readOnly={!canManageFlySchedule}
                            />
                          </div>
                        </>
                      ) : col.key === 'takeoffDate' ? (
                        <EditableCell
                          value={row[col.key]}
                          type="date"
                          onSave={newVal => handleInlineAtoCellSave(row, col.key, newVal)}
                          readOnly={!canManageFlySchedule}
                        />
                      ) : col.key === 'priority' ? (
                        <EditableCell
                          value={row[col.key]}
                          type="text"
                          inputProps={{ maxLength: 5 }}
                          validateAndNormalize={validatePriorityValue}
                          onSave={newVal => handleInlineAtoCellSave(row, col.key, newVal)}
                          readOnly={!canManageFlySchedule}
                        />
                      ) : col.key === 'acftStatus' ? (
                        <EditableCell
                          value={row[col.key]}
                          type="select"
                          inputProps={{ options: AIRCRAFT_STATUS_OPTIONS.filter(Boolean) }}
                          emptyLabel="--"
                          onSave={newVal => handleInlineAtoCellSave(row, col.key, newVal)}
                          readOnly={!canManageFlySchedule || Boolean(row.statusIsHistorical)}
                        />
                      ) : col.key === 'landingCode' ? (
                        <EditableCell
                          value={row[col.key]}
                          type="select"
                          inputProps={{
                            options: ['1', '2', '3'],
                          }}
                          emptyLabel="--"
                          onSave={newVal => handleInlineAtoCellSave(row, col.key, newVal)}
                          readOnly={!canManageFlySchedule}
                        />
                      ) : (
                        <EditableCell
                          value={row[col.key]}
                          type="text"
                          onSave={newVal => handleInlineAtoCellSave(row, col.key, newVal)}
                          readOnly={!canManageFlySchedule}
                        />
                      )}
                    </td>
                    )
                  );
                })}
                {/* Open-issue indicator cell */}
                <td style={{ textAlign: "center" }}>
                  <div className="ato-actions-grid">
                  {(() => {
                    const prediction = launchPredictionByAtoId.get(String(row.id));
                    return prediction ? (
                      <button
                        type="button"
                        className={`launch-success-badge is-${prediction.successBand || 'insufficient'}`}
                        title={`Chance to Make Launch: ${getLaunchPredictionBadgeLabel(prediction)}. ${prediction.recommendation || ''}`}
                        onClick={event => openLaunchPredictionDetails(row, prediction, event)}
                      >
                        {getLaunchPredictionBadgeLabel(prediction)}
                      </button>
                    ) : null;
                  })()}
                  {openAtoIds.has(String(row.id)) && (
                    <span
                      className="ato-action-icon ato-action-icon--warning"
                      role="img"
                      aria-label="Open issue"
                      title="Open issue"
                    >
                      ⚠️
                    </span>
                  )}                             
                  {canManageFlySchedule ? (
                  <>
                  <button
                    className="ato-action-icon"
                    onClick={e => handleDelete(row, e)}
                    title="Delete row"
                  >
                    🗑️
                  </button>
                  <button
                    className="ato-action-icon"
                    onClick={e => handleEditClick(row)}
                    title="Edit row"
                  >
                    ✏️
                  </button>
                  </>
                  ) : null}
                  </div>
                </td>
              </tr>
            )) : (
              <tr>
                <td
                  colSpan={visibleColumns.length + 2}
                  style={{ textAlign: 'center', padding: '12px' }}
                >
                  {hasActiveColumnFilters
                    ? 'No matching flights for the current filters.'
                    : 'No flights available for the selected date.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      </div>

      {showLaunchPresetModal && (
        <div className="modal-overlay">
          <div style={modalOverlayStyle}>
            <div style={modalContentStyle} className="ato-edit-modal launch-preset-modal">
              <div className="ato-edit-modal__header">
                <div>
                  <h3>Launch Presets</h3>
                  <p>Default scheduled milestones for {selectedBase || 'this location'}.</p>
                </div>
                <button
                  type="button"
                  className="ato-edit-modal__close"
                  onClick={() => setShowLaunchPresetModal(false)}
                  aria-label="Close Launch Presets"
                  title="Close"
                >
                  ×
                </button>
              </div>
              <div className="launch-preset-modal__body">
                <div className="commander-brief-notice">
                  Presets fill blank MX Show, Crew Show, Engine Start, and Taxi times from scheduled takeoff. Any manual value on a row or upload sheet overrides the preset and persists for that row.
                </div>
                <div className="launch-preset-modal__grid">
                  {LAUNCH_PRESET_FIELDS.map(field => (
                    <label key={field.key}>
                      <span>{field.label}</span>
                      <input
                        type="number"
                        min="0"
                        max="1440"
                        step="1"
                        value={launchPresetDraft[field.key] ?? DEFAULT_LAUNCH_PRESET[field.key]}
                        onChange={event => handleLaunchPresetDraftChange(field.key, event.target.value)}
                        disabled={launchPresetLoading || launchPresetSaving}
                      />
                      <small>{field.detail}</small>
                    </label>
                  ))}
                </div>
                <div className="launch-preset-modal__example">
                  <strong>Current example:</strong>
                  <span>
                    For a provided takeoff-time, MX Show is {launchPresetDraft.schedMxShowOffsetMinutes || 0} min prior,
                    Crew Show is {launchPresetDraft.schedCrewShowOffsetMinutes || 0} min prior,
                    Engine Start is {launchPresetDraft.schedEngStrtOffsetMinutes || 0} min prior,
                    and Taxi is {launchPresetDraft.schedTaxiOffsetMinutes || 0} min prior.
                  </span>
                </div>
                {launchPresetError ? (
                  <div className="commander-brief-error">{launchPresetError}</div>
                ) : null}
                <div className="ato-edit-modal__actions">
                  <button type="button" onClick={saveLaunchPreset} disabled={launchPresetSaving || launchPresetLoading}>
                    {launchPresetSaving ? 'Saving...' : 'Save Presets'}
                  </button>
                  <button type="button" onClick={() => setShowLaunchPresetModal(false)}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit ATO Modal */}
      {editModalOpen && (
        <div className="modal-overlay">
          <div style={modalOverlayStyle}>
            <div style={modalContentStyle} className="ato-edit-modal">
             <div className="ato-edit-modal__header">
               <h3>Edit ATO Entry</h3>
               <button
                 type="button"
                 className="ato-edit-modal__close"
                 onClick={() => setEditModalOpen(false)}
                 aria-label="Close Edit ATO Entry"
                 title="Close"
               >
                 ×
               </button>
             </div>
             <div className="ato-edit-modal__body">
             <section className="ato-edit-modal__actuals">
             {[
               { label: "Actual MX Show", key: "actMxShow" },
               { label: "Actual Crew Show ", key: "actCrewShow" },
               { label: "Actual Eng Start ", key: "actEngStrt" },
               { label: "Actual Taxi ", key: "actTaxi" },
               { label: "Actual Takeoff ", key: "actTakeoff" },
               { label: "Actual Land ", key: "actLand" }
             ].map(({ label, key }) => (
               <div key={key} className="ato-edit-modal__actual-row">
                 <label>{label}:</label>
                 <input
                   type="datetime-local"
                   value={editValues[key]}
                   onChange={e =>
                     setEditValues(v => ({ ...v, [key]: e.target.value }))
                   }
                 />
               </div>
             ))}
             </section>
              <section className="ato-modal-section ato-modal-section--compact ato-recovery-spot-section">
                <div className="ato-modal-section__header">
                  <strong>Recovery Parking</strong>
                </div>
                <div className="ato-recovery-spot-controls">
                  <input
                    id="ato-edit-recovery-spot"
                    type="text"
                    aria-label="Planned Recovery Spot"
                    value={editValues.recoverySpot || ''}
                    onChange={e => setEditValues(value => ({
                      ...value,
                      recoverySpot: e.target.value,
                      confirmRecoverySpot: false,
                    }))}
                    placeholder="Planned Recovery Spot"
                  />
                  <label className="ato-recovery-spot-confirm">
                    <input
                      type="checkbox"
                      checked={Boolean(editValues.confirmRecoverySpot)}
                      disabled={!editValues.actLand || !String(editValues.recoverySpot || '').trim()}
                      onChange={e => setEditValues(value => ({
                        ...value,
                        confirmRecoverySpot: e.target.checked,
                      }))}
                    />
                    Confirm aircraft recovered at this spot
                  </label>
                </div>
                <p className="ato-recovery-spot-note">
                  Planning is sortie-only. Confirmation after Actual Land updates only the current Status-tab spot.
                </p>
                {originalSnapshot.recoverySpotConfirmedAt ? (
                  <div className="ato-recovery-spot-confirmed">
                    Last confirmed {formatLocalTimestamp(originalSnapshot.recoverySpotConfirmedAt)}
                    {originalSnapshot.recoverySpotConfirmedBy ? ` by ${originalSnapshot.recoverySpotConfirmedBy}` : ''}.
                  </div>
                ) : null}
              </section>
              {/* launch and recovery personnel */}
              <section className="ato-modal-personnel-grid">
                <div className="ato-modal-personnel-card ato-modal-personnel-card--launch">
                  <div className="ato-modal-personnel-card__header">
                    <strong>Launch Personnel</strong>
                    <span>Launch-qualified and present</span>
                  </div>
                  <div className="ato-modal-personnel-list">
                    {personnelData
                      .filter(p => p["Present?"] === "Yes" && p["Launch?"] === "Yes")
                      .map(p => {
                        const empNum = getPersonnelEmployeeNumber(p);
                        const label = `${p["Last Name"]}, ${p["First Name"]} (${p["Shop Name"]})`;
                        const isSelected = personnelSelectionIncludes(editValues.launchPersonnel, empNum);
                        const canAssign = Boolean(empNum);
                        return (
                          <button
                            type="button"
                            key={p.id || empNum || label}
                            className={`ato-modal-personnel-option ${isSelected ? 'is-selected' : ''} ${canAssign ? '' : 'is-unavailable'}`}
                            disabled={!canAssign}
                            title={canAssign ? `Employee #${empNum}` : 'Add an employee number in Personnel before assigning this person.'}
                            onClick={() =>
                              setEditValues(v => {
                                const current = v.launchPersonnel || [];
                                const currentlySelected = personnelSelectionIncludes(current, empNum);
                                return {
                                  ...v,
                                  launchPersonnel: currentlySelected
                                    ? current.filter(val => String(val ?? '').trim() !== empNum)
                                    : [...current, empNum]
                                };
                              })
                            }
                          >
                            {label}
                          </button>
                        );
                      })}
                  </div>
                </div>

                <div className={`ato-modal-personnel-card ato-modal-personnel-card--recovery ${recoverySchedulingEnabled ? '' : 'is-premium-locked'}`}>
                  <div className="ato-modal-personnel-card__header">
                    <strong>Recovery Personnel</strong>
                    <span>{recoverySchedulingEnabled ? 'Recovery-qualified and present' : 'Premium scheduling add-on'}</span>
                  </div>
                  {recoverySchedulingEnabled ? (
                    <div className="ato-modal-personnel-list">
                      {personnelData
                        .filter(p => p["Present?"] === "Yes" && p["Recovery?"] === "Yes")
                        .map(p => {
                          const empNum = getPersonnelEmployeeNumber(p);
                          const label = `${p["Last Name"]}, ${p["First Name"]} (${p["Shop Name"]})`;
                          const isSelected = personnelSelectionIncludes(editValues.recoveryPersonnel, empNum);
                          const canAssign = Boolean(empNum);
                          return (
                            <button
                              type="button"
                              key={p.id || empNum || label}
                              className={`ato-modal-personnel-option ${isSelected ? 'is-selected' : ''} ${canAssign ? '' : 'is-unavailable'}`}
                              disabled={!canAssign}
                              title={canAssign ? `Employee #${empNum}` : 'Add an employee number in Personnel before assigning this person.'}
                              onClick={() =>
                                setEditValues(v => {
                                  const current = v.recoveryPersonnel || [];
                                  const currentlySelected = personnelSelectionIncludes(current, empNum);
                                  return {
                                    ...v,
                                    recoveryPersonnel: currentlySelected
                                      ? current.filter(val => String(val ?? '').trim() !== empNum)
                                      : [...current, empNum]
                                  };
                                })
                              }
                            >
                              {label}
                            </button>
                          );
                        })}
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="ato-premium-cell-button ato-modal-premium-button"
                      onClick={() => {
                        setNotification('Upgrade required to use Recovery Personnel Scheduling for this location.');
                        setTimeout(() => setNotification(''), 4000);
                      }}
                    >
                      Upgrade to schedule recovery personnel
                    </button>
                  )}
                </div>
              </section>
              {/* comments & fuel & cancelled */}
              <div className="ato-edit-modal__comments">
                <label htmlFor="ato-edit-comments">Comments:</label>
                <textarea
                  id="ato-edit-comments"
                  rows={2}
                  value={editValues.comments}
                  onChange={e =>
                    setEditValues(v => ({ ...v, comments: e.target.value }))
                  }
                />
              </div>
              <div className="ato-edit-modal__status-row">
                <div className="ato-edit-modal__fuel">
                  <label htmlFor="ato-edit-current-fuel">Current Fuel:</label>
                  <input
                    id="ato-edit-current-fuel"
                    type="text"
                    value={editValues.currentFuel || ''}
                    disabled={!editValues.statusRowId}
                    onChange={e =>
                      setEditValues(v => ({ ...v, currentFuel: e.target.value }))
                    }
                  />
                </div>
                <div className="ato-edit-modal__fuel-note">
                    {editValues.statusRowId
                      ? 'Saved to the Status tab. Fly Schedule Fuel Goal stays unchanged.'
                      : 'Tail not found in Status. Add this aircraft to Status to track current fuel.'}
                </div>
                <label className="ato-edit-modal__cancelled">
                  <input
                    type="checkbox"
                    checked={editValues.cancelled}
                    onChange={e =>
                      setEditValues(v => ({ ...v, cancelled: e.target.checked }))
                    }
                  />{" "}
                  Cancelled
                </label>
                {editValues.cancelled && (
                  <select
                    className="ato-edit-modal__cancel-reason"
                    value={editValues.cancelReason}
                    onChange={e =>
                      setEditValues(v => ({ ...v, cancelReason: e.target.value }))
                    }
                  >
                    <option value="">Select Reason</option>
                    {["Wx", "Mx", "Ops", "Other"].map(r => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <section className="ato-modal-section ato-modal-section--compact">
                <div className="ato-modal-section__header">
                  <strong>Spare Jet</strong>
                  <span>Line {editValues.lineNum || '--'} | Tail {editValues.tail || '--'}</span>
                </div>
                {editValues.spareSwappedIn ? (
                  <div className="ato-spare-swap-copy ato-spare-swap-copy--compact">
                    Using spare for original tail {editValues.originalTail || '--'}.
                    {editValues.spareSwapReason ? ` Reason: ${editValues.spareSwapReason}` : ''}
                  </div>
                ) : null}
                <div className="ato-modal-spare-swap ato-modal-spare-swap--compact">
                  <label className={`ato-spare-toggle ${editValues.isSpare ? 'is-spare' : ''}`}>
                    <input
                      type="checkbox"
                      checked={Boolean(editValues.isSpare)}
                      onChange={e => setEditValues(v => ({ ...v, isSpare: e.target.checked }))}
                    />
                    <span>{editValues.isSpare ? 'Made spare' : 'Make spare'}</span>
                  </label>
                  {!editValues.isSpare ? (
                    <>
                    <select
                      className="ato-spare-select"
                      value={swapSelectedSpareId}
                      onChange={e => setSwapSelectedSpareId(e.target.value)}
                    >
                      <option value="">Select spare aircraft</option>
                      {spareSwapOptions.map(row => (
                        <option key={row.id} value={row.id}>
                          {row.tail || 'No tail'} | line {row.lineNum || '--'} | spot {row.spot || '--'}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="ato-launch-header__assign ato-spare-confirm"
                      onClick={handleSubmitSpareSwap}
                      disabled={!swapSelectedSpareId}
                    >
                      Confirm Swap
                    </button>
                    <input
                      className="ato-spare-reason"
                      type="text"
                      value={swapReason}
                      onChange={e => setSwapReason(e.target.value)}
                      placeholder="Reason / trigger for swap"
                    />
                    {spareSwapOptions.length === 0 ? (
                      <div className="ato-spare-swap-warning">
                        No spares available on takeoff date.
                      </div>
                    ) : null}
                    </>
                  ) : null}
                </div>
              </section>
              {/* buttons */}
              <div className="ato-edit-modal__actions">
                <button onClick={handleSaveEdit}>Save</button>
                <button onClick={() => setEditModalOpen(false)}>
                  Cancel
                </button>
              </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <LaunchSuccessAdvisorModal
        prediction={selectedLaunchPrediction}
        feedback={launchFeedback}
        onFeedbackChange={setLaunchFeedback}
        onSaveFeedback={submitLaunchPredictionFeedback}
        canManageWeatherOverride={canOverrideLaunchWeather}
        isWeatherOverrideSaving={isWeatherOverrideSaving}
        onWeatherOverrideChange={submitWeatherOverride}
        onClose={() => setSelectedLaunchPrediction(null)}
      />

        {/* Notification ribbon */}
        {notification && (
          <div style={notificationStyle}>
            {notification}
          </div>
        )}
      </div>
    );
}

